import { Component, OnInit, Input, ChangeDetectionStrategy } from '@angular/core';
import { ModalController, AlertController } from '@ionic/angular/lazy';
import { EventService } from 'src/app/services/event.service';
import { ObserverService } from 'src/app/services/observer.service';
import { CartService } from 'src/app/services/cart.service';
import { StorageService } from 'src/app/services/storage.service';
import { PermissionService } from 'src/app/services/permission.service';
import moment from 'moment-timezone';

@Component({
    selector: 'app-select-event-ticket-type',
    templateUrl: './select-event-ticket-type.page.html',
    styleUrls: ['./select-event-ticket-type.page.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SelectEventTicketTypePage implements OnInit {

    cart = null;
    moment = moment;
    @Input() event_id = null;
    event = null;
    ticket_types = null;

    constructor(
        public modalController: ModalController,
        public alertController: AlertController,
        private eventService: EventService,
        private observerService: ObserverService,
        private cartService: CartService,
        private storageService: StorageService,
        public permissionService: PermissionService,
    ) { }

    ngOnInit() {

        this.eventService.getEvent(this.event_id).then((event) => {
            this.event = event;
        });

        this.cartService.get().then((cart) => {

            this.cart = cart;

            // NOTE: load ticket types once the cart has loaded, as the sell price needs the cart (cart comes from native storage, which is slower than IndexedDB on Android)
            this.eventService.getEventTicketTypes(this.event_id).then((ticket_types: any) => {

                ticket_types.sort((a, b) => {
                    if (a.order < b.order) { return -1; }
                    if (a.order > b.order) { return 1; }
                    return 0;
                });

                for (let i = 0; i < ticket_types.length; i++) {
                    ticket_types[i] = this.addSellPriceToEventTicketType(ticket_types[i]);
                }

                this.ticket_types = ticket_types;

            });

        })
        .catch(() => {});

        this.observerService.subscribe('ticket_type_added|select_event_ticket_type_modal', (ticket_type) => {
            if (ticket_type) {
                ticket_type = this.addSellPriceToEventTicketType(ticket_type);
                this.ticket_types.push(ticket_type);
                // NOTE: we don't re-sort at this point, as it would clause jank
            }
        });

        this.observerService.subscribe('ticket_type_deleted|select_event_ticket_type_modal', (ticket_type) => {
            if (ticket_type) {
                for (let i = 0; i < this.ticket_types.length; i++) {
                    if (this.ticket_types[i].id === ticket_type["id"]) {
                        this.ticket_types.splice(i, 1);
                        break;
                    }
                }
            }
        });

        this.observerService.subscribe('ticket_type_updated|select_event_ticket_type_modal', (ticket_type) => {
            if (ticket_type) {
                for (let i = 0; i < this.ticket_types.length; i++) {
                    if (this.ticket_types[i].id === ticket_type["id"]) {
                        ticket_type = this.addSellPriceToEventTicketType(ticket_type);
                        this.ticket_types[i] = ticket_type;
                        break;
                    }
                }
            }
        });

    }

    ngOnDestroy() {
        this.observerService.destroy("ticket_type_added|select_event_ticket_type_modal");
        this.observerService.destroy("ticket_type_deleted|select_event_ticket_type_modal");
        this.observerService.destroy("ticket_type_updated|select_event_ticket_type_modal");
    }

    addSellPriceToEventTicketType(ticket_type) {
        delete ticket_type['sell_price'];
        if(ticket_type['prices']) {
            for (let i = 0; i < ticket_type['prices'].length; i++) {
                if (ticket_type['prices'][i].currency.id === this.cart['currency'].id) {
                    for (let j = 0; j < ticket_type['prices'][i].pricing_tiers.length; j++) {
                        if (ticket_type['prices'][i].pricing_tiers[j].id === this.cart['pricing_tier'].id) {
                            ticket_type['sell_price'] = ticket_type['prices'][i].pricing_tiers[j].price;
                            break;
                        }
                    }
                    break;
                }
            }
        }
        return ticket_type;
    }

    addTicketType(ticket_type_id) {
        this.storageService.get("close_ticket_type_modal").then((close_ticket_type_modal) => {
            this.cartService.addTicketTypeItem(ticket_type_id).then(() => {
                if (close_ticket_type_modal && close_ticket_type_modal == true) {
                    this.modalController.dismiss();
                }
            })
            .catch(() => { });
        })
        .catch((error) => {
            console.error(error)
        });
    }

    ticketTypeClicked(ticket_type) {
        if (ticket_type.tickets_left_count) {
            let tickets_left = ticket_type.tickets_left_count;
            this.cartService.get().then((cart) => {
                if (cart && cart['items'].length) {
                    for (let i = 0; i < cart['items'].length; i++) {
                        if (cart['items'][i].id == ticket_type.id) {
                            tickets_left = (tickets_left - cart['items'][i].quantity);
                            break;
                        }
                    }
                }
                if (tickets_left <= 0) {
                    if (!this.permissionService.isPermitted('pos-sell-oversell-item')) {
                        this.presentAlert('Insufficient permissions', "You don't have permission to oversell this item.");
                    }
                    else {
                        this.storageService.get("alert_if_overselling_item").then((alert_if_overselling_item) => {
                            if (alert_if_overselling_item == true) {
                                this.presentOversellingTicketConfirmation(ticket_type.id);
                            }
                            else {
                                this.addTicketType(ticket_type.id);
                            }
                        })
                        .catch((error) => {
                            console.error(error)
                        });
                    }
                }
                else {
                    this.addTicketType(ticket_type.id);
                }
            })
            .catch((error) => {
                console.error(error)
            });
        }
        else {
            this.addTicketType(ticket_type.id);
        }
    }

    close() {
        this.modalController.dismiss();
    }

    async presentOversellingTicketConfirmation(ticket_type_id) {
        const alert = await this.alertController.create({
            header: 'No tickets left',
            message: 'Are you sure you want to oversell this item?',
            buttons: [
                {
                    text: 'Cancel',
                    handler: () => {
                        this.storageService.get("close_ticket_type_modal").then((close_ticket_type_modal) => {
                            if (close_ticket_type_modal && close_ticket_type_modal == true) {
                                this.modalController.dismiss();
                            }
                        })
                        .catch((error) => {
                            console.error(error)
                        });
                    }
                },
                {
                    text: 'Yes',
                    handler: () => {
                        this.addTicketType(ticket_type_id);
                    }
                }
            ],
        });
        await alert.present();
    }

    async presentAlert(header, message) {
        const alert = await this.alertController.create({
            header: header,
            message: message,
            buttons: ["OK"],
        });
        await alert.present();
    }

}
